import { useCatalogVariantPreview } from "@/hooks/catalog-variant-preview";
import type { CatalogListItem } from "@/lib/catalog-item";
import { variantTilesFor, type CatalogVariantGroup } from "@/lib/catalog-item-variants";
import { ItemVariantTiles, type CatalogVariantTile } from "@/components/ui/item-variant-tiles";

// The owner of one card-or-row's variant preview: the selection, the lazy read of
// a sibling's details, and the tile strip that drives both.
//
// **A render-prop component**, which is unusual here and earns it. The two views
// need the *same* four values around genuinely different markup — a card stacks
// the strip under a centred picture, a row puts it in a 128px column beside the
// text — so neither can own the other's layout, and the piece they share is
// state, not shape. The donor solves this by having the two files hold identical
// copies of the state (`item-card.tsx:77-89` and `item-data-row.tsx:70-82`, the
// same eight lines twice); one owner here is the same behaviour without the
// second copy to keep in step.
//
// It also keeps `ItemCard` and `ItemListRows` free of a query, which is what lets
// them go on being rendered in tests without a client.

/** The groups as the list response carries them, beside the results. */
export type CatalogVariantGroups = readonly CatalogVariantGroup<CatalogVariantTile>[];

/**
 * The preview's state itself, for a view that draws its own strip — the Item
 * Search island's card (#798), whose 28px strip is a different donor's from the
 * catalog's `ItemVariantTiles`.
 */
export interface ItemVariantPreviewState {
  /** The card's group, as the list response carries it — empty when ungrouped. */
  members: readonly CatalogVariantTile[];
  /** Which tile is pressed — the sibling picked, whether or not it has landed. */
  selectedVariantId: number;
  /** Which tile's read is in flight, if any. */
  loadingVariantId: number | undefined;
  /** That read failed, and is not being retried. */
  failed: boolean;
  onSelect: (variantId: number) => void;
}

interface ItemVariantPreviewProps {
  /** The card's own item — the one the list matched. */
  item: CatalogListItem;
  variantGroups?: CatalogVariantGroups;
  children: (view: {
    /** The item to draw: the previewed sibling, or the own item. */
    item: CatalogListItem;
    /** The strip, or `null` for an ungrouped item — pass it straight through. */
    tiles: React.ReactNode;
    /** The same preview, unpainted — for a view that draws its own strip. */
    state: ItemVariantPreviewState;
  }) => React.ReactNode;
}

export function ItemVariantPreview({ item, variantGroups, children }: ItemVariantPreviewProps) {
  const preview = useCatalogVariantPreview(item);
  const members = variantTilesFor(item, variantGroups);

  return children({
    item: preview.displayItem,
    state: {
      members,
      selectedVariantId: preview.selectedVariantId,
      loadingVariantId: preview.loadingVariantId,
      failed: preview.failed,
      onSelect: preview.onSelect,
    },
    tiles:
      members.length === 0 ? null : (
        <ItemVariantTiles
          members={members}
          selectedVariantId={preview.selectedVariantId}
          loadingVariantId={preview.loadingVariantId}
          onSelect={preview.onSelect}
          // `sx={{ alignSelf: 'start' }}` — the strip is left-aligned inside a
          // column that centres everything else, on both views.
          className="self-start"
        />
      ),
  });
}
